import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, FileBarChart2, Bell, Building2, UserCog, LogOut, Sun, Moon, Menu } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/advances", label: "Advances", icon: ArrowDownToLine, testid: "nav-advances" },
  { to: "/repayments", label: "Repayments", icon: ArrowUpFromLine, testid: "nav-repayments" },
  { to: "/bad-debt", label: "Bad Debt", icon: AlertTriangle, testid: "nav-bad-debt" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testid: "nav-reports" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/branches", label: "Branches", icon: Building2, testid: "nav-branches" },
  { to: "/account", label: "Account", icon: UserCog, testid: "nav-account" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav_ = useNavigate();
  const [dark, setDark] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("plutus_theme");
    const isDark = saved ? saved === "dark" : true;
    setDark(isDark);
    document.documentElement.classList.toggle("dark", isDark);
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("plutus_theme", next ? "dark" : "light");
  };

  const doLogout = async () => {
    await logout();
    nav_("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside data-testid="sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static z-40 w-64 shrink-0 h-screen border-r border-border bg-card transition-transform duration-200`}>
        <div className="p-5 border-b border-border flex items-center gap-3">
          <img src="/plutus_logo.png" alt="PLUTUS" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-serif text-2xl leading-none tracking-tight">PLUTUS</div>
            <div className="overline mt-1">Advance Ledger</div>
          </div>
        </div>
        <nav className="p-3 space-y-0.5">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} end={n.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive ? "gold-bg font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }>
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
          <div className="px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button className="md:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="sidebar-toggle">
                <Menu className="w-5 h-5" />
              </button>
              <div className="overline">Internal · Owner Console</div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={toggleTheme} data-testid="theme-toggle">
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <span className="hidden md:inline text-sm text-muted-foreground">{user?.username}</span>
              <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="p-6 md:p-8 flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
